import { useEffect, useState } from 'react';
import axios from 'axios';
import { GraduationCap, BookOpen, Check, Loader2, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_ORDER = { Cadet: 1, Officer: 2, Captain: 3, Commander: 4 };

export default function CadetAcademy() {
  const { user, authHeaders } = useAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState({ enrollments: [] });
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    const [m, p] = await Promise.all([
      axios.get(`${API}/cadet/modules`),
      user ? axios.get(`${API}/cadet/my-progress`, { headers: authHeaders(), withCredentials: true }) : Promise.resolve({ data: { enrollments: [] } }),
    ]);
    setModules(m.data);
    setProgress(p.data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const enrollment = (modId) => progress.enrollments.find((e) => e.module_id === modId);

  const enroll = async (modId) => {
    if (!user) { toast.error('Sign in to enroll.'); return; }
    setBusy(modId);
    try {
      await axios.post(`${API}/cadet/enroll`, { module_id: modId }, { headers: authHeaders(), withCredentials: true });
      await loadAll();
      toast.success('Enrolled. Begin lesson 1.');
    } catch { toast.error('Enrollment refused.'); }
    setBusy(null);
  };

  const markLesson = async (modId, lessonIdx, completed) => {
    setBusy(`${modId}-${lessonIdx}`);
    try {
      await axios.post(`${API}/cadet/progress`,
        { module_id: modId, lesson_index: lessonIdx, completed },
        { headers: authHeaders(), withCredentials: true });
      await loadAll();
    } catch { toast.error('Progress sync failed.'); }
    setBusy(null);
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <GraduationCap size={14} strokeWidth={1.5} /> EIGOO Academy · Mastery Vault
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Training under<br />
            <span className="font-display italic text-[#D4AF37]">Mercy&apos;s seal.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Armored Blueprints for the Sovereign Relocation Fortress.
            Relocation Mastery first; Property Mastery as the deeper craft.
            <span className="text-[#D4AF37]"> 10% of every course routes to the CyberKids Anchor</span> —
            fueling the next-generation pipeline.
          </p>
        </div>
      </div>

      {user && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 border-t border-b border-[#D4AF37]/15 py-8">
          <Stat label="Enrolled" value={progress.total_enrolled || 0} />
          <Stat label="Completed" value={progress.total_completed || 0} />
          <Stat label="Modules Available" value={modules.length} />
          <Stat label="Current Tier" value="Cadet" />
        </div>
      )}

      {!user && (
        <div className="sov-border bg-[#0A0A0A] p-6 mb-10 flex items-center justify-between flex-wrap gap-4">
          <p className="text-sm text-[#A3A3A3]">Sign in to enroll and track your progress under sovereign seal.</p>
          <Link to="/login" className="btn-sovereign"><span>Sign In</span> <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
        </div>
      )}

      {/* Render module categories — Relocation Mastery first, then Property Mastery */}
      {['Relocation Mastery', 'Property Mastery'].map((category) => {
        const inCat = modules
          .filter((m) => (m.category || 'Property Mastery') === category)
          .sort((a, b) => (TIER_ORDER[a.tier] - TIER_ORDER[b.tier]) || a.order - b.order);
        if (inCat.length === 0) return null;
        const isRelocation = category === 'Relocation Mastery';
        return (
          <section key={category} data-testid={`academy-vault-${category.toLowerCase().replace(/\s+/g, '-')}`} className="mb-16">
            <div className="flex items-baseline justify-between gap-4 flex-wrap mb-6 pb-4 border-b border-[#D4AF37]/20">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">
                  {isRelocation ? 'Relocation Mastery Vault · Armored Blueprints' : 'Property Mastery · Foundational Drills'}
                </div>
                <h2 className="text-3xl md:text-4xl font-extralight tracking-tight">
                  {isRelocation ? <>The <span className="font-display italic text-[#D4AF37]">UK Exit</span> curriculum.</> : <>The <span className="font-display italic text-[#D4AF37]">Property Cadet</span> curriculum.</>}
                </h2>
              </div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{inCat.length} module{inCat.length === 1 ? '' : 's'}</span>
            </div>

            <div className="space-y-4">
              {inCat.map((m) => {
                const e = enrollment(m.id);
                const pct = e ? Math.round((e.lessons_completed.length / e.lessons_total) * 100) : 0;
                return (
                  <div key={m.id} data-testid={`cadet-module-${m.code}`} className={`sov-border ${isRelocation ? 'bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#D4AF37]/5' : 'bg-[#0A0A0A]'} overflow-hidden`}>
                    <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">{m.code}</span>
                          <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{m.tier} · {m.duration_minutes}m</span>
                          {(m.cyberkids_pct || 0) > 0 && (
                            <span className="text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 px-2 py-0.5">
                              {m.cyberkids_pct}% → CyberKids Anchor
                            </span>
                          )}
                        </div>
                        <h3 className="text-2xl font-light tracking-tight mb-2">{m.title}</h3>
                        <p className="text-sm text-[#A3A3A3] font-light leading-relaxed">{m.tagline}</p>
                      </div>
                      <div className="md:w-52 text-right">
                        {(m.price_usd || 0) > 0 && !e && (
                          <div className="font-display text-3xl text-[#D4AF37] mb-2">${m.price_usd}</div>
                        )}
                        {e ? (
                          <div>
                            <div className="font-display text-3xl text-[#D4AF37]">{pct}%</div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1">{e.lessons_completed.length}/{e.lessons_total} lessons</div>
                            <div className="h-[2px] bg-[#1a1a1a] mt-3 overflow-hidden">
                              <div className="h-full bg-[#D4AF37] transition-all duration-700" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => enroll(m.id)}
                            disabled={busy === m.id || !user}
                            data-testid={`cadet-enroll-${m.code}`}
                            className="btn-sovereign"
                          >
                            {busy === m.id ? <Loader2 size={14} className="animate-spin" /> : <><span>{(m.price_usd || 0) > 0 ? 'Acquire' : 'Enroll'}</span> <ArrowUpRight size={14} strokeWidth={1.5} /></>}
                          </button>
                        )}
                      </div>
                    </div>
                    {e && (
                      <div className="border-t border-[#D4AF37]/15 px-6 md:px-8 py-5 space-y-3">
                        {m.lessons.map((lesson, i) => {
                          const done = e.lessons_completed.includes(i);
                          return (
                            <button
                              key={i}
                              onClick={() => markLesson(m.id, i, !done)}
                              data-testid={`cadet-lesson-${m.code}-${i}`}
                              className="w-full flex items-center gap-4 py-2 text-left hover:bg-[#D4AF37]/5 transition-colors px-3 -mx-3"
                            >
                              <span className={`flex-shrink-0 w-4 h-4 border flex items-center justify-center ${done ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-[#444]'}`}>
                                {done && <Check size={10} className="text-[#050505]" strokeWidth={2.5} />}
                              </span>
                              <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] w-8">{String(i + 1).padStart(2, '0')}</span>
                              <span className={`text-sm font-light flex-1 ${done ? 'text-[#A3A3A3] line-through' : 'text-[#F5F5F5]'}`}>{lesson}</span>
                              <BookOpen size={12} className={done ? 'text-[#D4AF37]' : 'text-[#555]'} strokeWidth={1.5} />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {/* CyberKids Anchor disclosure band */}
      <section data-testid="cyberkids-anchor" className="sov-border bg-[#0A0A0A] p-8 md:p-10 mt-8 text-center">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">CyberKids Anchor</div>
        <h3 className="text-2xl md:text-3xl font-extralight max-w-3xl mx-auto leading-[1.25]">
          <span className="font-display italic text-[#D4AF37]">10%</span> of every Mastery Vault course
          fuels the CyberKids youth pipeline.
        </h3>
        <p className="text-sm text-[#A3A3A3] mt-5 max-w-2xl mx-auto font-light">
          This is the lineage anchor — Junior Admiral vaults, Ashairah&apos;s Business Forge, and the
          next generation&apos;s sovereign education are funded directly by these enrollments.
        </p>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    <div className="metric-number text-3xl md:text-4xl">{value}</div>
  </div>
);
